import { QueryClient, QueryFunction } from "@tanstack/react-query";

export class ApiError extends Error {
  status: number;
  data?: unknown;
  correlationId?: string;

  constructor(params: {
    status: number;
    message: string;
    data?: unknown;
    correlationId?: string | null;
  }) {
    super(params.message);
    this.status = params.status;
    this.data = params.data;
    this.correlationId =
      typeof params.correlationId === "string" && params.correlationId.trim().length
        ? params.correlationId
        : undefined;
  }
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const raw = await res.text();
    let parsed: unknown;
    if (raw) {
      try {
        parsed = JSON.parse(raw);
      } catch {
        parsed = undefined;
      }
    }
    const correlationId =
      (parsed &&
        typeof parsed === "object" &&
        parsed !== null &&
        "correlationId" in parsed &&
        typeof (parsed as any).correlationId === "string"
        ? (parsed as any).correlationId
        : undefined) || res.headers.get("x-correlation-id");
    const message =
      (parsed &&
        typeof parsed === "object" &&
        parsed !== null &&
        typeof (parsed as any).message === "string" &&
        (parsed as any).message.trim().length
        ? (parsed as any).message
        : undefined) ||
      raw?.trim() ||
      res.statusText ||
      "Request failed";
    throw new ApiError({
      status: res.status,
      message,
      data: parsed ?? raw,
      correlationId,
    });
  }
}

export async function apiRequest<T = any>(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<T> {
  const res = await fetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  
  // If it's a DELETE request with a 204 response (no content)
  if (method === "DELETE" && res.status === 204) {
    return {} as T;
  }
  
  // Safely parse the JSON response
  try {
    const contentType = res.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await res.json() as T;
    } else {
      // If not JSON, try to get the text response
      const text = await res.text();
      console.warn('Response is not JSON:', text.substring(0, 100) + '...');
      // For non-JSON responses, return an empty object rather than failing
      return {} as unknown as T;
    }
  } catch (error) {
    console.error('Error parsing response:', error);
    // Return a minimal valid response
    return {} as unknown as T;
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey[0] as string, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    
    // Safely parse the JSON response
    try {
      const contentType = res.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await res.json();
      } else {
        // If not JSON, try to get the text response
        const text = await res.text();
        console.warn('Query response is not JSON:', text.substring(0, 100) + '...');
        // For non-JSON responses, return an empty object rather than failing
        return {} as unknown as any;
      }
    } catch (error) {
      console.error('Error parsing query response:', error);
      // Return a minimal valid response
      return {} as unknown as any;
    }
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
